/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import WebGLRenderGraph from "App/render/webgl/render_graph";
import Renderable from "App/render/webgl/renderables/renderable";
import WebGLContext from "App/web_gl_context";
import {logError, logInfo} from "App/debug";
import Factory from "App/render/webgl/renderables/factory";
import IScene from "js_interface/src/scene_interface";

export default
class Shader extends Renderable {

    public static TypeName: string = "Shader";
    private _requiredRenderables: string[] = [];
    private program: WebGLProgram;
    private vertex_shader: WebGLShader;
    private vertex_shader_attached: boolean;
    private fragment_shader: WebGLShader;
    private fragment_shader_attached: boolean;
    private vertex_shader_preproc: string;
    private vertex_shader_declarations: string;
    private vertex_shader_varyings: string;
    private vertex_shader_program: string;
    private fragment_shader_preproc: string;
    private fragment_shader_declarations: string;
    private fragment_shader_varyings: string;
    private fragment_shader_program: string;

    private uniformLocations: { [key: string]: WebGLUniformLocation; };
    private attributeLocations: { [key: string]: GLint; };

    private vertex_shader_names: string[];
    private fragment_shader_names: string[];

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph, null);

        this.init();
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): Shader {
        return new Shader(aSceneAPI, aRenderGraph);
    }

    public getUniformLocations(): { [key: string]: WebGLUniformLocation; } {
        return this.uniformLocations;
    }

    public getAtttributesLocations(): { [key: string]: GLint; } {
        return this.attributeLocations;
    }

    public getProgram(): WebGLProgram {
       return this.program;
    }

    public setRenderablesFilters(aFilters: string[]) {
        this._requiredRenderables = aFilters.slice();
    }

    public resetRenderablesFilters() {
        this._requiredRenderables = [];
    }

    public getRenderablesFilters(): string[] {
        return this._requiredRenderables;
    }

    public init() {
        this.program = null;
        this.vertex_shader = null;
        this.vertex_shader_attached = false;

        this.fragment_shader = null;
        this.fragment_shader_attached = false;

        this.vertex_shader_preproc = WebGLContext.isWebGL2 ?
            "#version 300 es" + "\n" +
            "#define WEBGL2" + "\n" +
            "#define attribute in" + "\n" +
            "#define varying out" + "\n"
            : "";
        this.vertex_shader_declarations = "";
        this.vertex_shader_varyings = "";
        this.vertex_shader_program = "";

        this.fragment_shader_preproc = WebGLContext.isWebGL2 ?
            "#version 300 es" + "\n" +
            "#define WEBGL2" + "\n" +
            "#define varying in" + "\n" +
            "#define gl_FragColor outputColor" + "\n" +
            "#define texture2D texture" + "\n"
            : "";
        this.fragment_shader_declarations = "";
        this.fragment_shader_varyings = "";
        this.fragment_shader_program = WebGLContext.isWebGL2 ? "#ifdef DO_NOT_OUTPUT_COLOR" + "\n #else \n out highp vec4 outputColor; \n #endif \n" : "";

        //this.uniforms = [];
        //this.attributes = [];

        this.uniformLocations = {};
        this.attributeLocations = {};

        // To check if shader was already loaded
        this.vertex_shader_names = [];
        this.fragment_shader_names = [];
    }

    public release() {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        if (this.vertex_shader) {
            if (this.vertex_shader_attached) {
                gl.detachShader(this.program, this.vertex_shader);
            }
            gl.deleteShader(this.vertex_shader);
            this.vertex_shader = null;
            this.vertex_shader_attached = false;
        }

        if (this.fragment_shader) {
            if (this.fragment_shader_attached) {
                gl.detachShader(this.program, this.fragment_shader);
            }
            gl.deleteShader(this.fragment_shader);
            this.fragment_shader = null;
            this.fragment_shader_attached = false;
        }

        if (this.program) {
            gl.deleteProgram(this.program);
        }

        this.init();
    }

    public attachVertexShader(shader: any) {//name: string, preproc: string, declaration: string, varyings: string, implementation: string ) {
        if (this.vertex_shader_names.indexOf(shader.name) !== -1) {
            return;
        }

        this.vertex_shader_preproc += shader.preproc;
        this.vertex_shader_declarations += shader.declaration;
        this.vertex_shader_varyings += shader.varyings;
        this.vertex_shader_program += shader.implementation;
        this.vertex_shader_names.push(shader.name);
    }

    public addPreprocessorDefinition(aDefinition: string) {
        this.fragment_shader_preproc += aDefinition + "\n";
        this.vertex_shader_preproc += aDefinition + "\n";
    }

    public attachFragmentShader(shader: any) {//name: string, preproc: string, declaration: string, varyings: string, implementation: string) {
        if (this.fragment_shader_names.indexOf(shader.name) !== -1) {
            return;
        }

        this.fragment_shader_preproc += shader.preproc;
        this.fragment_shader_declarations += shader.declaration;
        this.fragment_shader_varyings += shader.varyings;
        this.fragment_shader_program += shader.implementation;
        this.fragment_shader_names.push(shader.name);
    }

    public compile() {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        this.program = gl.createProgram();

        this.vertex_shader = gl.createShader(gl.VERTEX_SHADER);

        // logInfo(this.vertex_shader_declarations + this.vertex_shader_varyings + this.vertex_shader_program);

        gl.shaderSource(this.vertex_shader, this.vertex_shader_preproc + this.vertex_shader_declarations + this.vertex_shader_varyings + this.vertex_shader_program);
        gl.compileShader(this.vertex_shader);
        let success = gl.getShaderParameter(this.vertex_shader, gl.COMPILE_STATUS);
        if (!success) {
            // Something went wrong during compilation; get the error
            let sources =   "";
            for (let i = 0; i != this.vertex_shader_names.length; i++) {
                sources += this.vertex_shader_names[i] + ", ";
            }
            logError("could not compile vertex shader (" + sources + "):" + gl.getShaderInfoLog(this.vertex_shader));

            let shaderLines = (this.vertex_shader_preproc + this.vertex_shader_declarations + this.vertex_shader_varyings + this.vertex_shader_program).split("\n");
            for (let i = 0; i < shaderLines.length; i++) {
                logInfo(i + ": " + shaderLines[i]);
            }

            return;
        }
        gl.attachShader(this.program, this.vertex_shader);
        this.vertex_shader_attached = true;

        this.fragment_shader = gl.createShader(gl.FRAGMENT_SHADER);

        //logInfo(this.fragment_shader_declarations + this.fragment_shader_varyings + this.fragment_shader_program);

        gl.shaderSource(this.fragment_shader, this.fragment_shader_preproc + this.fragment_shader_declarations + this.fragment_shader_varyings + this.fragment_shader_program);
        gl.compileShader(this.fragment_shader);
        success = gl.getShaderParameter(this.fragment_shader, gl.COMPILE_STATUS);
        if (!success) {
            // Something went wrong during compilation; get the error
            let sources = "";
            for (let i = 0; i != this.fragment_shader_names.length; i++) {
                sources += this.fragment_shader_names[i] + ", ";
            }
            logError("could not compile fragment shader (" + sources + "):" + gl.getShaderInfoLog(this.fragment_shader));

            let shaderLines = (this.fragment_shader_preproc + this.fragment_shader_declarations + this.fragment_shader_varyings + this.fragment_shader_program).split("\n");
            for (let i = 0; i < shaderLines.length; i++) {
                logInfo(i + ": " + shaderLines[i]);
            }
            return;
        }
        gl.attachShader(this.program, this.fragment_shader);
        this.fragment_shader_attached = true;

        // this is because on Mac, the index 0 have to be defined (used) otherwise the following error message occurs:
        // PERFORMANCE WARNING: Attribute 0 is disabled. This has significant performance penalty
        // https://github.com/mrdoob/three.js/issues/4364
        // http://stackoverflow.com/questions/14976011/performance-warning-processing-js
        //
        // The bindAttribLocation causes warning when linking the program:
        //  Could not find vertex shader attribute 'a_position' to match BindAttributeLocation request.
        // but this should not affect the performance
        //gl.bindAttribLocation(this.program, 0, 'a_position');

        gl.linkProgram(this.program);

        let msg = gl.getProgramInfoLog(this.program);
        if (msg && msg.length > 4) { // >4 is here because chrome returns "    " string when there is no error :(
            let sources = "";
            for (let i = 0; i != this.vertex_shader_names.length; i++) {
                sources += this.vertex_shader_names[i] + ", ";
            }

            for (let i = 0; i != this.fragment_shader_names.length; i++) {
                sources += this.fragment_shader_names[i] + ", ";
            }
            logError(msg + " in " + sources);
        }

        this.initUniforms();
        this.initAttributes();

        // delete the vertex and fragment shaders as the program is already linked
        //gl.deleteShader(this.vertex_shader); /* <~ Can I do this now? */
        //gl.deleteShader(this.fragment_shader);
    }

    public initUniforms() {
        let getLocation = (name: string, program: WebGLProgram): WebGLUniformLocation => {
            return (<WebGLContext>this._context).getWebGLRenderingContext().getUniformLocation(program, name);
        };

        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();
        let numUniforms = gl.getProgramParameter(this.program, gl.ACTIVE_UNIFORMS);
        for (let ii = 0; ii < numUniforms; ++ii) {
            let uniformInfo = gl.getActiveUniform(this.program, ii);
            if (!uniformInfo) {
                continue;
            }
            this.uniformLocations[uniformInfo.name] = getLocation(uniformInfo.name, this.program);
        }
    }

    public initAttributes() {
        let getLocation = (name: string, program: WebGLProgram): GLint => {
            return (<WebGLContext>this._context).getWebGLRenderingContext().getAttribLocation(program, name);
        };

        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();
        let numAttribs = gl.getProgramParameter(this.program, gl.ACTIVE_ATTRIBUTES);
        for (let ii = 0; ii < numAttribs; ++ii) {
            let attribInfo = gl.getActiveAttrib(this.program, ii);
            if (!attribInfo) {
                continue;
            }
            this.attributeLocations[attribInfo.name] = getLocation(attribInfo.name, this.program);
        }
    }

    public enableAttributes() {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();
        for (let key in this.attributeLocations) {
            gl.enableVertexAttribArray(this.attributeLocations[key]);
        }
    }

    public disableAttributes() {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();
        for (let key in this.attributeLocations) {
            gl.disableVertexAttribArray(this.attributeLocations[key]);
        }
    }

    public render() {
        (<WebGLContext>this._context).getWebGLRenderingContext().useProgram(this.program);
    }

    public finishRender() {
        (<WebGLContext>this._context).getWebGLRenderingContext().useProgram(null);
    }
}

Factory.register(Shader);
